import AuthApi from '@/api/storefront/AuthApi';
import AddressApi from '@/api/hybris/AddressApi';
import AnalyticsApi from '@/api/hybris/AnalyticsApi';
import BookingApi from '@/api/hybris/BookingApi';
import AutoCompleteApi from '@/api/hybris/AutoCompleteApi';
import CampaignApi from '@/api/hybris/CampaignApi';
import CartAddressApi from '@/api/hybris/CartAddressApi';
import CartApi from '@/api/hybris/CartApi';
import CartDeliveryModeApi from '@/api/hybris/CartDeliveryModeApi';
import CartEntryApi from '@/api/hybris/CartEntryApi';
import CartPaymentModeApi from '@/api/hybris/CartPaymentModeApi';
import CartPaybackApi from '@/api/hybris/CartPaybackApi';
import CartStoreApi from '@/api/hybris/CartStoreApi';
import CartVoucherApi from '@/api/hybris/CartVoucherApi';
import ConfigurationApi from '@/api/hybris/ConfigurationApi';
import ContactFormApi from '@/api/hybris/ContactFormApi';
import ContentTileApi from '@/api/hybris/ContentTileApi';
import CountryApi from '@/api/hybris/CountryApi';
import DonationsApi from '@/api/hybris/DonationsApi';
import FileApi from '@/api/hybris/FileApi';
import ForgottenPasswordApi from '@/api/hybris/ForgottenPasswordApi';
import NavigationNodesHybrisApi from '@/api/hybris/NavigationNodesApi';
import OrderApi from '@/api/hybris/OrderApi';
import PageHybrisApi from '@/api/hybris/PageApi';
import PaybackApi from '@/api/hybris/PaybackApi';
import Personalization from '@/api/hybris/Personalization';
import PetApi from '@/api/hybris/PetApi';
import PickupStationApi from '@/api/hybris/PickupStationApi';
import ProductApi from '@/api/hybris/ProductApi';
import SeoContentApi from '@/api/hybris/SeoContentApi';
import SheltersApi from '@/api/hybris/SheltersApi';
import StoreApi from '@/api/hybris/StoreApi';
import UserApi from '@/api/hybris/UserApi';
import NewsletterApi from '@/api/hybris/NewsletterApi';
import SubscriptionApi from '@/api/hybris/SubscriptionApi';
import RaffleApi from '@/api/hybris/RaffleApi';
import RedirectApi from '@/api/hybris/RedirectApi';
import VotingApi from '@/api/hybris/VotingApi';
import WishlistApi from '@/api/hybris/WishlistApi';
import WishPetApi from '@/api/hybris/WishPetApi';
import CustomerApi from '@/api/hybris/fn/CustomerApi';
import AbstractApi, { type ClientConfig } from '@/api/AbstractApi';
import type { NuxtApp } from '#app';

export default defineNuxtPlugin({
  name: 'api',
  dependsOn: ['axios', 'pinia', 'init-base-store'],
  parallel: true,
  setup(nuxtApp) {
    const { $config, $router, $i18n, $pinia, $domain, $axios } = nuxtApp as NuxtApp;
    const { baseStore } = useMainStore();

    // Injecting certain values due to unsolved import restrictions within `./api/` directory
    const config = { ...$config, client: $axios, baseStore };

    // Simple cache storage for API instances
    const apiInstances: Record<string, AbstractApi> = {};

    // Returns an API instance from cache or creates a new one
    const getApiInstance = <T extends AbstractApi>(ApiClass: any): T =>
      (apiInstances[(ApiClass as any).name] ||= new (ApiClass as new (config: ClientConfig) => T)(config)) as T;

    const api = {
      hybris: {
        get address() {
          return getApiInstance<AddressApi>(AddressApi);
        },
        get analytics() {
          return getApiInstance<AnalyticsApi>(AnalyticsApi);
        },
        get booking() {
          return getApiInstance<BookingApi>(BookingApi);
        },
        get autoComplete() {
          return getApiInstance<AutoCompleteApi>(AutoCompleteApi);
        },
        get campaign() {
          return getApiInstance<CampaignApi>(CampaignApi);
        },
        get cartAddress() {
          return getApiInstance<CartAddressApi>(CartAddressApi);
        },
        get cart() {
          return getApiInstance<CartApi>(CartApi);
        },
        get cartDeliveryMode() {
          return getApiInstance<CartDeliveryModeApi>(CartDeliveryModeApi);
        },
        get cartEntry() {
          return getApiInstance<CartEntryApi>(CartEntryApi);
        },
        get cartPaymentMode() {
          return getApiInstance<CartPaymentModeApi>(CartPaymentModeApi);
        },
        get cartPayback() {
          return getApiInstance<CartPaybackApi>(CartPaybackApi);
        },
        get cartStore() {
          return getApiInstance<CartStoreApi>(CartStoreApi);
        },
        get cartVoucher() {
          return getApiInstance<CartVoucherApi>(CartVoucherApi);
        },
        get configuration() {
          return getApiInstance<ConfigurationApi>(ConfigurationApi);
        },
        get contactForm() {
          return getApiInstance<ContactFormApi>(ContactFormApi);
        },
        get contentTiles() {
          return getApiInstance<ContentTileApi>(ContentTileApi);
        },
        get country() {
          return getApiInstance<CountryApi>(CountryApi);
        },
        get donation() {
          return getApiInstance<DonationsApi>(DonationsApi);
        },
        get file() {
          return getApiInstance<FileApi>(FileApi);
        },
        get forgottenPassword() {
          return getApiInstance<ForgottenPasswordApi>(ForgottenPasswordApi);
        },
        get navigationNodes() {
          return getApiInstance<NavigationNodesHybrisApi>(NavigationNodesHybrisApi);
        },
        get newsletter() {
          return getApiInstance<NewsletterApi>(NewsletterApi);
        },
        get order() {
          return getApiInstance<OrderApi>(OrderApi);
        },
        get page() {
          return getApiInstance<PageHybrisApi>(PageHybrisApi);
        },
        get payback() {
          return getApiInstance<PaybackApi>(PaybackApi);
        },
        get personalization() {
          return getApiInstance<Personalization>(Personalization);
        },
        get pet() {
          return getApiInstance<PetApi>(PetApi);
        },
        get pickupStation() {
          return getApiInstance<PickupStationApi>(PickupStationApi);
        },
        get product() {
          return getApiInstance<ProductApi>(ProductApi);
        },
        get raffle() {
          return getApiInstance<RaffleApi>(RaffleApi);
        },
        get redirect() {
          return getApiInstance<RedirectApi>(RedirectApi);
        },
        get seocontent() {
          return getApiInstance<SeoContentApi>(SeoContentApi);
        },
        get shelters() {
          return getApiInstance<SheltersApi>(SheltersApi);
        },
        get store() {
          return getApiInstance<StoreApi>(StoreApi);
        },
        get subscription() {
          return getApiInstance<SubscriptionApi>(SubscriptionApi);
        },
        get user() {
          return getApiInstance<UserApi>(UserApi);
        },
        get voting() {
          return getApiInstance<VotingApi>(VotingApi);
        },
        get wishlist() {
          return getApiInstance<WishlistApi>(WishlistApi);
        },
        get wishpet() {
          return getApiInstance<WishPetApi>(WishPetApi);
        },
      },
      hybrisFn: {
        get customer() {
          return getApiInstance<CustomerApi>(CustomerApi);
        },
      },
      storefront: {
        get auth() {
          return getApiInstance<AuthApi>(AuthApi);
        },
      },
      setAccessToken(accessToken?: string) {
        for (const [key, hybrisApi] of Object.entries(this.hybris)) {
          if (!ACCESS_TOKEN_WHITELIST.includes(key)) {
            hybrisApi.setAccessToken(accessToken);
          }
        }
      },
      getHeader() {
        logger.debug('Loading header...');
        const { language } = useMainStore($pinia);
        const { ts } = $router.currentRoute.value.query;
        return this.hybris.page.find('header', null, ts?.toString(), language, true);
      },
      getFooter() {
        logger.debug('Loading footer...');
        const { language } = useMainStore($pinia);
        const { ts } = $router.currentRoute.value.query;
        return this.hybris.page.find('footer', null, ts?.toString(), language, true);
      },
      async getPage(path: string, cache = true) {
        logger.debug('Loading page...');

        const mainStore = useMainStore($pinia);
        const currentRoute = unref(useRouter().currentRoute);
        const { revId, ts } = $router.currentRoute.value.query;

        let pathCleaned = path;

        if (pathCleaned.startsWith('/')) {
          pathCleaned = pathCleaned.substring(1);
        }

        if (pathCleaned === '') {
          pathCleaned = 'homepage';
        }

        return this.hybris.page
          .find(pathCleaned, revId?.toString(), ts?.toString(), mainStore.language, cache)
          .then((response) => {
            if (!response?.data?.content) {
              return {};
            }

            if (response.data.author) {
              delete response.data.author;
            }

            const datalayerStore = useDatalayerStore($pinia);
            datalayerStore.$patch({
              filter: null,
              products: null,
              search: null,
              error: null,
            });

            const page = response.data;

            if (typeof page.content === 'string') {
              try {
                page.content = JSON.parse(page.content);
              } catch (error) {
                logger.error('Error occurred while JSON.parse()', error);
              }
            }

            const hreflangInfo = page.hrefLangData || [];

            if (hreflangInfo.length) {
              mainStore.hrefLang = hreflangInfo;
            } else {
              mainStore.hrefLang = null;
            }

            if (page.content.author) {
              delete page.content.author;
            }

            if (!page) {
              throw createError({
                statusMessage: $i18n.t('pages.error.message', 404),
                statusCode: 404,
              });
            }

            let pageType = getPageType(page);
            let pagePath = getPagePath(page);

            if (currentRoute.path === $i18n.t('url.homepage') && page.url === $i18n.t('productRecalls.url')) {
              pageType = 'homepage';
              pagePath = [$i18n.t('url.homepage'), $i18n.t('productRecalls.url')];
            }

            datalayerStore.addPageProperties({
              category: getPageCategory(page),
              pageType,
              path: pagePath,
            });

            return page;
          })
          .catch(async (error) => {
            if (isResponseError(error, 503)) {
              return navigateTo(`https://${$domain}/maintenance/`, { external: true });
            }

            // Check if there is an actual redirect for the given path.
            if (isResponseError(error, 404)) {
              const result = await this.getRedirect(path);

              if (!result) {
                return;
              }

              logger.debug('Page loading caused redirect:', {
                from: pathCleaned,
                to: result.redirectTo,
              });

              return navigateTo(result.redirectTo, {
                external: true,
                redirectCode: result.statusCode,
              });
            }

            const statusMessage = import.meta.dev
              ? error.toString()
              : $i18n.t('pages.error.message', [error?.response?.status]);

            logger.error('Page loading failed:', statusMessage);

            throw createError({
              statusMessage,
              statusCode: error?.response?.status,
            });
          });
      },
      async getNodes(path: string, sorted = false) {
        logger.debug(`Loading navigation nodes...`);
        const { language } = useMainStore($pinia);

        return this.hybris.navigationNodes
          .find(`/${path.replace(SLASHES_REGEX, '')}/`, language, sorted)
          .then((response) => response.data)
          .catch((error) => {
            if (!isResponseError(error, 404)) {
              logger.error('Navigation Nodes loading failed:', error.response ?? error);
            }
            return {};
          });
      },
      async getRedirect(path: string) {
        logger.debug(`Loading page redirect...`);

        return this.hybris.redirect
          .find(`/${path.replace(SLASHES_REGEX, '')}/`)
          .then((response) => response.data)
          .catch((error) => {
            if (!isResponseError(error, 404)) {
              logger.error('Page redirect loading failed:', error.response ?? error);
            }
            return false;
          });
      },
    };

    return {
      provide: {
        api,
      },
    };
  },
});
